import { displays, generateUniqueKey, isStringNotEmpty } from '@/utils'

const options = {
  [displays.mobile]: {
    length: 3,
    duration: 5,
    method: 'unshift'
  },
  [displays.desktop]: {
    length: 6,
    duration: 10,
    method: 'push'
  }
}

/**
 * Состояние управляемого таймера автоскрытия уведомления.
 * @typedef {object} SnackbarTimer
 * @property {number | undefined} timeoutId Идентификатор таймера, undefined на паузе.
 * @property {number} startedAt Момент последнего запуска таймера в миллисекундах.
 * @property {number} remaining Остаток времени до автоскрытия в миллисекундах.
 * @property {boolean} paused Признак паузы.
 */

/**
 * Таймеры автоскрытия уведомлений по идентификатору уведомления.
 * @type {Map<string, SnackbarTimer>}
 */
const timers = new Map()

/**
 * Запускает таймер автоскрытия уведомления.
 * @param {string} id Идентификатор уведомления.
 * @param {number} remaining Время до автоскрытия в миллисекундах.
 * @param {Function} callback Обработчик автоскрытия.
 */
function startTimer(id, remaining, callback) {
  timers.set(id, {
    timeoutId: setTimeout(() => {
      timers.delete(id)

      return callback()
    }, remaining),
    startedAt: Date.now(),
    remaining,
    paused: false
  })
}

/**
 * Снимает таймер автоскрытия уведомления.
 * @param {string} id Идентификатор уведомления.
 * @returns {boolean} Признак того, что таймер существовал и был снят.
 */
function clearTimer(id) {
  const timer = timers.get(id)
  if (!timer) {
    return false
  }

  clearTimeout(timer.timeoutId)
  timers.delete(id)

  return true
}

/**
 * Снимает таймер автоскрытия и собственный интервал уведомления.
 * @param {object} snackbar Уведомление из очереди.
 */
function clearSnackbarTimers(snackbar) {
  clearTimer(snackbar.id)
  if (snackbar.interval) {
    clearInterval(snackbar.interval)
  }
}

export default {
  namespaced: true,

  state: {
    snackbars: []
  },

  mutations: {
    addSnackbar(state, { snackbar, display }) {
      if (state.snackbars.length >= options[display].length) {
        let index = 0
        if (display === displays.mobile) {
          index = -1
        }
        const [ displaced ] = state.snackbars.splice(index, 1)
        clearSnackbarTimers(displaced)
      }
      state.snackbars[options[display].method](snackbar)
    },

    removeSnackbar(state, id) {
      if (isStringNotEmpty(id)) {
        const index = state.snackbars.findIndex(item => {
          return item.id === id
        })
        if (~index) {
          state.snackbars.splice(index, 1)
        }
      }
    },

    clearSnackbars(state) {
      state.snackbars.forEach(item => {
        clearSnackbarTimers(item)
      })
      state.snackbars = []
    }
  },

  actions: {
    addSnackbar({ dispatch, commit, rootGetters }, payload) {
      let display = displays.desktop
      if (rootGetters['viewport/viewport'].breakpoint.smDown) {
        display = displays.mobile
      }

      const id = generateUniqueKey()
      const remove = () => {
        dispatch('removeSnackbar', id)
      }
      const duration = payload.duration || options[display].duration

      if (payload.button && payload.button.on && typeof payload.button.on.click === 'function') {
        const callback = payload.button.on.click
        payload.button.on.click = () => {
          remove()
          callback()
        }
      }

      const snackbar = {
        id,
        text: payload.text,
        type: payload.type,
        button: payload.button,
        classes: payload.classes,
        cancelable: payload.cancelable ?? true,
        remove: () => {
          dispatch('removeSnackbar', id)
        },
        pause: () => {
          dispatch('pauseSnackbar', id)
        },
        resume: () => {
          dispatch('resumeSnackbar', id)
        },
        interval: payload.interval,
        duration
      }

      if (!payload.interval) {
        startTimer(id, duration * 1000, () => {
          return dispatch('removeSnackbar', id)
        })
      }

      commit('addSnackbar', {
        snackbar,
        display
      })
    },

    pauseSnackbar(context, id) {
      const timer = timers.get(id)
      if (!timer || timer.paused) {
        return false
      }

      clearTimeout(timer.timeoutId)
      timer.timeoutId = undefined
      timer.remaining = timer.remaining - (Date.now() - timer.startedAt)
      timer.paused = true

      return true
    },

    resumeSnackbar({ dispatch }, id) {
      const timer = timers.get(id)
      if (!timer || !timer.paused) {
        return false
      }

      startTimer(id, timer.remaining, () => {
        return dispatch('removeSnackbar', id)
      })

      return true
    },

    removeSnackbar({ state, commit }, id) {
      const index = state.snackbars.findIndex(item => {
        return item.id === id
      })
      if (~index) {
        clearSnackbarTimers(state.snackbars[index])
        setTimeout(() => {
          return commit('removeSnackbar', id)
        }, 150)
      }
    },

    clearSnackbars({ commit }) {
      for (const id of [ ...timers.keys() ]) {
        clearTimer(id)
      }
      commit('clearSnackbars')
    }
  },

  getters: {
    snackbars: state => {
      return state.snackbars
    }
  }
}
